import InputField from '@/components/_rebrand/InputField';
import PhoneField from '@/components/_rebrand/PhoneField';
import {
  HubspotFieldLabels,
  HubspotFieldNames
} from '@/constants/hubspot';
import { UserContext } from '@/context/user';
import { getFormikFieldErrorFunction } from '@/lib/formik';
import { FormikProps } from 'formik';
import { FC, useContext, useEffect } from 'react';
import { ButtonElement } from '../Button';

export type RequestDemoFormStepTwoFields = {
  [HubspotFieldNames.CompanyName]: string;
  [HubspotFieldNames.CompanySize]: string;
  [HubspotFieldNames.Email]: string;
  [HubspotFieldNames.FirstName]: string;
  [HubspotFieldNames.Industry]: string;
  [HubspotFieldNames.JobTitle]: string;
  [HubspotFieldNames.LastName]: string;
  [HubspotFieldNames.PrimaryUsecase]: string;
  [HubspotFieldNames.Phone]: string;
  [HubspotFieldNames.MarketingEmailOptOut]: boolean;
  /*
   * Hidden fields for tracking Qualified meeting status - values should only be sent if the meeting is not offered
   * If a meeting WAS offered, the values are sent to HS via workflows in Qualified
   */
  [HubspotFieldNames.QualifiedBookingStatus]?: string;
  [HubspotFieldNames.MostRecentQualifiedMeetingOfferedStatus]?: string;
  [HubspotFieldNames.MostRecentQualifiedBookingStatus]?: string;
  [HubspotFieldNames.QualifiedMeetingOffered]?: string;
  [HubspotFieldNames.QualifiedMeetingNotOfferedReason]?: string;
}

export type FormStepProps = {
  formik: FormikProps<RequestDemoFormStepTwoFields>;
}

const RequestDemoFormStepTwo: FC<FormStepProps> = ({ formik }) => {
  const { getTempUserData } = useContext(UserContext);

  const getFieldError = getFormikFieldErrorFunction(formik);
  const firstNameError = getFieldError(HubspotFieldNames.FirstName);
  const lastNameError = getFieldError(HubspotFieldNames.LastName);
  const phoneError = getFieldError(HubspotFieldNames.Phone);
  const companyNameError = getFieldError(HubspotFieldNames.CompanyName);

  const isSubmitDisabled =
    formik.isValidating ||
    formik.isSubmitting ||
    !!firstNameError ||
    !!lastNameError ||
    !!phoneError ||
    !!companyNameError;

  useEffect(() => {
    const data = getTempUserData();

    Object.keys(data).forEach((key) => {
      formik.setFieldValue(key, data[key]);
    });
  }, []);

  return (
    <form id="demo-form-step-two" className="flex flex-col" onSubmit={formik.handleSubmit} noValidate>
      <fieldset className="flex flex-col gap-lg py-xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-md">
          <InputField
            type="text"
            name={HubspotFieldNames.FirstName}
            label={HubspotFieldLabels.FirstName}
            placeholder="Jamie"
            value={formik.values[HubspotFieldNames.FirstName]}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            errorMessage={firstNameError}
          />

          <InputField
            type="text"
            name={HubspotFieldNames.LastName}
            label={HubspotFieldLabels.LastName}
            placeholder="Smith"
            value={formik.values[HubspotFieldNames.LastName]}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            errorMessage={lastNameError}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-md">
          <InputField
            type="text"
            name={HubspotFieldNames.CompanyName}
            label={HubspotFieldLabels.CompanyName}
            placeholder="Your Company"
            value={formik.values[HubspotFieldNames.CompanyName]}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            errorMessage={companyNameError}
          />
          <PhoneField
            name={HubspotFieldNames.Phone}
            label={HubspotFieldLabels.Phone}
            value={formik.values[HubspotFieldNames.Phone]}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            errorMessage={phoneError}
          />
        </div>
      </fieldset>
      <ButtonElement
        disabled={isSubmitDisabled}
        text={formik.isValidating || formik.isSubmitting ? 'Submitting' : 'Continue'}
        variant="primary"
        buttonType="filled"
      />
    </form>
  );
};

export default RequestDemoFormStepTwo;
